import { ref, reactive, onMounted, nextTick } from 'vue';

/**
 * SwipeTrap
 *
 * A composable (composition function) that provides a method to trap focus within a modal
 *
 * This is designed to be used with the FocusTrap component
 * @see https://github.com/posva/focus-trap-vue
 *
 * To use:
 * - Add composable to a component (or root app component)
 * - Define the ref e.g.:
 * setup() {
 *   const focusTrap = ref(null);
 *
 *   return {
 *     focusTrap,
 *   }
 * }
 *
 * - Add the 'focusTrap' ref to the focus trap component
 *  e.g. `<focus-trap ref="focusTrap">`
 */
export default function useSwipeTrap(focusTrap) {
  const interactives = reactive([
    'a[href]',
    'button',
    'input:not([type="hidden"])',
    'textarea',
    'select',
    'details',
    '[tabindex="0"]',
  ]);
  const ignoredAncestors = reactive([
    'script',
    'noscript',
    'style',
    'data',
    'body',
  ]);
  const existingTabIndex = ref([]);
  const existingAriaHidden = ref([]);
  const triggerElement = ref(null);

  const cached = reactive({
    pageInteractives: null,
    modalInteractives: null,
    nonModalInteractives: null,
    siblings: [],
  });

  /**
   * Get all interactive elements on the page
   *
   * @return {Array} An array of interactive elements
   */
  const pageInteractives = () => {
    if (!cached.pageInteractives) {
      cached.pageInteractives = Array.from(document.querySelectorAll(interactives.join(', ')));
    }

    return cached.pageInteractives;
  };

  /**
   * Get all interactive elements within the modal
   *
   * @return {Array} An array of interactive elements
   */
  const modalInteractives = () => {
    if (!cached.modalInteractives) {
      cached.modalInteractives = Array.from(triggerElement.value.querySelectorAll(interactives.join(', ')));
    }

    return cached.modalInteractives;
  };

  /**
   * Get all interactive elements outside the modal
   *
   * @return {Array} An array of interactive elements
   */
  const nonModalInteractives = () => {
    if (!cached.nonModalInteractives) {
      cached.nonModalInteractives = Array.from(pageInteractives())
        .filter((interactive) => !modalInteractives().includes(interactive));
    }

    return cached.nonModalInteractives;
  };

  /**
   * Get all sibling elements of the modal, including the items outside of #app
   *
   * @return {Array} An array of sibling elements
   */
  const siblings = () => {
    if (cached.siblings.length) {
      return cached.siblings;
    }

    // define the elements that should be ignored when finding siblings
    const notIgnored = (element) => !ignoredAncestors.includes(element.tagName.toLowerCase());
    // get the sibling elements of the modal
    const siblingsFiltered = Array.from(triggerElement.value.parentNode.children)
      .filter((sibling) => sibling !== triggerElement.value && notIgnored(sibling));
    // traverse up the dom to the body to find all ancestor siblings too
    let parent = triggerElement.value.parentNode;
    // define the elements that we are interested in
    const isSibling = (sibling) => sibling !== parent && notIgnored(sibling);
    // check if the sibling is an ignored ancestor or parent, if so we don't want it
    // loop through the ancestors until we reach the root
    while (parent !== document.body) {
      siblingsFiltered.push(...Array.from(parent.parentNode.children).filter(isSibling));
      parent = parent.parentNode;
    }

    // store the cache
    cached.siblings = siblingsFiltered;

    return cached.siblings;
  };

  const activate = () => {
    nonModalInteractives().forEach((interactive) => {
      if (interactive.hasAttribute('tabindex')) {
        existingTabIndex.value.push({
          element: interactive,
          value: interactive.getAttribute('tabindex'),
        });
      }
      interactive.setAttribute('tabindex', '-1');
    });

    siblings().forEach((sibling) => {
      if (sibling.hasAttribute('aria-hidden')) {
        existingAriaHidden.value.push({
          element: sibling,
          value: sibling.getAttribute('aria-hidden'),
        });
      }
      sibling.setAttribute('aria-hidden', 'true');
    });
  };

  const deactivate = () => {
    // remove the tabindex attributes from the page interactives
    nonModalInteractives().forEach((interactive) => {
      interactive.removeAttribute('tabindex');

      // restore the previous tabindex value if it was set
      const existingTabIndexFiltered = existingTabIndex.value.find((tabIndex) => tabIndex.element === interactive);
      if (existingTabIndexFiltered) {
        interactive.setAttribute('tabindex', existingTabIndexFiltered);
      }
    });

    // remove the aria-hidden attribute from the modal siblings
    siblings().forEach((sibling) => {
      sibling.removeAttribute('aria-hidden');

      // restore the previous aria-hidden value if it was set
      const existingAriaHiddenFiltered = existingAriaHidden.value.find((ariaHidden) => ariaHidden.element === sibling);
      if (existingAriaHiddenFiltered) {
        sibling.setAttribute('aria-hidden', existingAriaHiddenFiltered);
      }
    });
  };

  onMounted(() => {
    nextTick(() => {
      if (!focusTrap.value) {
        return;
      }

      // Assign this.$el equivalent
      triggerElement.value = focusTrap.value.$el;

      // Check if we are on the root level
      if (focusTrap.value.$el === triggerElement.value.parentNode) {
        triggerElement.value = focusTrap.value.$el;
      }
    });
  });

  return {
    activate,
    deactivate,
  };
};
